import React from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../../components/page/PageHeader';
import WikiFrame from '../shared/WikiFrame';
import WikiIcon from '../shared/WikiIcon';
import { WikiError, WikiLoading } from '../shared/WikiStates';
import { useWikiQuery, wikiKeys } from '../shared/useWikiQueries';
import { wikiPaths } from '../shared/wikiRoutes';
import { dateLabel, versionLabel, wikiPathLabel } from '../shared/wikiFormat';
import './wikiAck.css';

/**
 * To acknowledge — route `/wiki/acknowledge` (spec A §1.6).
 *
 * Every required page the caller can read (`GET /wiki/acknowledge?scope=all`),
 * in the prototype's data order, each with an "Acknowledged" (green) or
 * "Needs you" (orange) pill. A row opens the page — acknowledging happens on
 * the page's ack bar, never here (the prototype has no inline button).
 * "You're all caught up." sits under the list once nothing is pending; when no
 * page is required at all, the empty list card is dropped (spec §1.6).
 *
 * Key `['wiki','ack','all']`: an acknowledgement invalidates the 'ack' scope
 * (with 'home', 'counts' and the page), so the pill, the Home panel and the
 * nav count flip together.
 */

export const WIKI_ACK_SUBTITLE = "Pages your admins marked as required reading. When a page changes, you're asked to confirm the new version.";
export const WIKI_ACK_CAUGHT_UP = "You're all caught up.";

function rowMeta(page) {
    const path = page.path || wikiPathLabel(page.category_name, page.subcategory_name);
    const updated = dateLabel(page.updated_at);
    return [path, versionLabel(page.version), updated ? `updated ${updated}` : '']
        .filter(Boolean)
        .join(' · ');
}

function AckRow({ page }) {
    const done = Boolean(page.acknowledged);
    return (
        <Link
            to={wikiPaths.page(page.page_id)}
            className="wk-list-row wk-ack-row"
            data-testid={`wiki-ack-row-${page.page_id}`}
        >
            <WikiIcon name="file-text" size={16} className="wk-ack-row-icon" />
            <span className="wk-ack-row-text">
                <span className="wk-t-row wk-ack-row-name">{page.title}</span>
                <span className="wk-t-meta wk-ack-row-meta">{rowMeta(page)}</span>
            </span>
            <span
                className={`wk-pill ${done ? 'wk-pill--green' : 'wk-pill--orange'}`}
                data-testid={`wiki-ack-status-${page.page_id}`}
            >
                {done ? 'Acknowledged' : 'Needs you'}
            </span>
        </Link>
    );
}

export default function WikiAck() {
    const query = useWikiQuery(wikiKeys.ack('all'), '/wiki/acknowledge?scope=all');
    const pages = Array.isArray(query.data?.pages) ? query.data.pages : [];
    const pendingCount = typeof query.data?.count_pending === 'number'
        ? query.data.count_pending
        : pages.filter((page) => !page.acknowledged).length;

    let body;
    if (query.isLoading) {
        body = <div className="wk-card"><WikiLoading label="Loading required pages…" /></div>;
    } else if (query.isError) {
        body = (
            <div className="wk-card">
                <WikiError error={query.error} onRetry={() => query.refetch()} />
            </div>
        );
    } else {
        body = (
            <div className="wk-ack-body">
                {pages.length > 0 && (
                    <div className="wk-card wk-card--flush wk-ack-list" data-testid="wiki-ack-list">
                        {pages.map((page) => <AckRow key={page.page_id} page={page} />)}
                    </div>
                )}
                {pendingCount === 0 && (
                    <p className="wk-t-dense wk-ack-done" role="status" data-testid="wiki-ack-done">
                        {WIKI_ACK_CAUGHT_UP}
                    </p>
                )}
            </div>
        );
    }

    return (
        <WikiFrame testId="wiki-ack">
            <PageHeader title="To acknowledge" subtitle={WIKI_ACK_SUBTITLE} />
            {body}
        </WikiFrame>
    );
}
